// Hit flash for the bespoke rig path.
//
// The shared instanced humanoids flash via setColorAt (one insertion point in the renderer's
// setInstance helper). Bespoke rigs — mounts, beasts, per-faction workers and squads, buildings,
// and the twelve boss rigs — are THREE.Groups of individual meshes with their own materials, so
// they need a different mechanism: briefly raise each material's emissive.
//
// The material list is cached on the group at first use rather than traversed every frame. Rigs
// are built per squad member, so a per-frame traverse across a full battlefield would be a real
// cost for an effect that is off most of the time.
//
// Safe to mutate these materials in place: factionSquadRigs/mountRigs/etc. allocate a fresh
// MeshStandardMaterial per call, so no material is shared between rigs. (The instanced path is
// the opposite — STYLES hands out cached Colors — which is why that side copies into a scratch.)

import * as THREE from 'three';

interface FlashEntry {
  material: THREE.MeshStandardMaterial;
  baseEmissive: THREE.Color;
  baseIntensity: number;
}

interface FlashCache {
  entries: FlashEntry[];
  /** last applied amount, so we can skip redundant writes and restore exactly once */
  applied: number;
}

const CACHE_KEY = '__hitFlash';

function cacheFor(group: THREE.Object3D): FlashCache {
  const existing = (group.userData as Record<string, unknown>)[CACHE_KEY] as FlashCache | undefined;
  if (existing) return existing;
  const entries: FlashEntry[] = [];
  group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      // MeshBasicMaterial has no emissive; skip rather than crash on the UI-ish bits of a rig.
      if (!(material instanceof THREE.MeshStandardMaterial)) continue;
      entries.push({
        material,
        baseEmissive: material.emissive.clone(),
        baseIntensity: material.emissiveIntensity,
      });
    }
  });
  const cache: FlashCache = { entries, applied: 0 };
  (group.userData as Record<string, unknown>)[CACHE_KEY] = cache;
  return cache;
}

const FLASH_COLOR = new THREE.Color(0xffffff);

/**
 * Apply a 0..1 flash to a bespoke rig. Cheap and idempotent: when the amount is unchanged
 * (usually 0) nothing is written.
 */
export function applyRigFlash(group: THREE.Object3D, amount: number): void {
  const cache = cacheFor(group);
  const clamped = Math.max(0, Math.min(1, amount));
  if (clamped === cache.applied) return;
  cache.applied = clamped;
  for (const entry of cache.entries) {
    if (clamped <= 0) {
      entry.material.emissive.copy(entry.baseEmissive);
      entry.material.emissiveIntensity = entry.baseIntensity;
    } else {
      // Lerp toward white rather than overwriting, so rigs that are *already* emissive (boss
      // auras, Astral constructs, crystal cores) brighten instead of losing their own colour.
      entry.material.emissive.copy(entry.baseEmissive).lerp(FLASH_COLOR, clamped);
      entry.material.emissiveIntensity = entry.baseIntensity + clamped * 1.6; // TEST
    }
  }
}

/** Drop the cache when a rig is disposed, so its materials are not retained. */
export function clearRigFlashCache(group: THREE.Object3D): void {
  delete (group.userData as Record<string, unknown>)[CACHE_KEY];
}
